// src/pages/OfflineQueueReview.js
//
// The "needs review" side of offline mode's sync engine (this feature's own
// plan doc, "Real Offline Mode for the Cashier/Checkout Screen") —
// offlineSync.js's drainQueue() never auto-retries a genuine (non-network)
// failure forever; it marks the queue item "failed" and leaves it here for
// a person to look at and either retry (once whatever was wrong is fixed)
// or discard outright. Deliberately reads offlineDb directly rather than
// through any backend endpoint — this queue lives entirely on the till that
// created it, so there's nothing server-side to fetch.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import PageHeader from "../components/UI/PageHeader";
import { notify } from "../utils/notify";
import { useTranslation } from "../context/I18nContext";
import { useOffline } from "../context/OfflineContext";
import { getQueueItems, deleteQueueItem, updateQueueItem } from "../services/offlineDb";

const TYPE_LABELS = {
  open_shift: "Open Shift",
  sale: "Sale",
  close_shift: "Close Shift",
};

export default function OfflineQueueReview() {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { syncNow, refreshQueueState } = useOffline();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const all = await getQueueItems();
      setItems(all);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const failed = items.filter((i) => i.status === "failed");
  const active = items.filter((i) => i.status !== "failed");

  const handleDiscard = async (id) => {
    await deleteQueueItem(id);
    notify.success(t("offline_queue_discarded", "Discarded"));
    await load();
    await refreshQueueState();
  };

  const handleRetry = async (id) => {
    // Puts it back to "pending" so the next drainQueue() pass picks it up
    // again — useful once whatever caused the failure (a real conflict, a
    // bad payload) has actually been fixed elsewhere.
    await updateQueueItem(id, { status: "pending", error: null });
    await load();
    await syncNow();
    await load();
    await refreshQueueState();
  };

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto transition-all duration-300">
      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/checkout")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Checkout size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>
              {t("offline_queue_title", "Offline Sync Queue")}
            </h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">
              {t("offline_queue_subtitle", "Sales and shift actions recorded while offline")}
            </p>
          </div>
        </div>
        <Button variant="primary" icon={ICONS.Checkout} onClick={() => syncNow().then(load)}>
          {t("offline_queue_sync_now", "Sync Now")}
        </Button>
      </PageHeader>

      {loading ? (
        <div className="p-8 text-center text-gray-400">{t("loading_text", "Loading…")}</div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-gray-400">{t("offline_queue_empty", "Nothing queued — everything is synced")}</div>
      ) : (
        <div className="space-y-8">
          {failed.length > 0 && (
            <section>
              <h3 className="text-sm font-bold uppercase tracking-wide text-red-500 mb-3">
                {t("offline_queue_needs_review", "Needs review")}
              </h3>
              <div className="space-y-3">
                {failed.map((item) => (
                  <div key={item.id} className="bg-white rounded-2xl border border-red-100 p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
                    <div>
                      <div className="flex items-center gap-2 text-sm font-bold text-gray-800">
                        <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[10px] uppercase">
                          {TYPE_LABELS[item.type] || item.type}
                        </span>
                        <span className="text-gray-400 font-mono text-xs">{new Date(item.createdAt).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-red-600 mt-1">{item.error}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => handleRetry(item.id)}>{t("offline_queue_retry", "Retry")}</Button>
                      <Button variant="danger" size="sm" onClick={() => handleDiscard(item.id)}>{t("offline_queue_discard", "Discard")}</Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {active.length > 0 && (
            <section>
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-400 mb-3">
                {t("offline_queue_pending", "Waiting to sync")}
              </h3>
              <div className="space-y-3">
                {active.map((item) => (
                  <div key={item.id} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3 justify-between">
                    <div className="flex items-center gap-2 text-sm font-bold text-gray-800">
                      <span className="px-2 py-0.5 rounded-full bg-gray-50 text-gray-500 text-[10px] uppercase">
                        {TYPE_LABELS[item.type] || item.type}
                      </span>
                      <span className="text-gray-400 font-mono text-xs">{new Date(item.createdAt).toLocaleString()}</span>
                    </div>
                    <span className="text-xs font-bold text-gray-400 uppercase">{item.status}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
